import React, { useContext, useLayoutEffect, useState } from 'react'

import { Observable } from '@apollo/client'

import { DateNavigation, ResolvableMeta } from 'modules/core/lib/navigation/routes'
import { Meta } from 'modules/core/lib/navigation/types'

import { useMetaContext } from './matches'

export type ResolvedMeta = {
  pathname: string
  data: Meta
}

export type RouteMetaWithPathname = {
  pathname: string
  meta: ResolvableMeta<Meta>
  resource: boolean
  filters?: {
    date?: DateNavigation
  }
}

interface ResponsivePageContext {
  routeMeta: RouteMetaWithPathname | null
  setActionHandler: (actionHandler: (key: string) => void) => void
}

const ResponsivePageContext = React.createContext<ResponsivePageContext | undefined>(undefined)
export const ResponsivePageProvider = ResponsivePageContext.Provider

export const useResponsivePage = () => {
  const context = useContext(ResponsivePageContext)

  if (context === undefined) {
    throw 'useResponsivePage must be used within a ResponsivePageContext'
  }

  return context
}

export function useRouteMeta(routeMeta: RouteMetaWithPathname | null): ResolvedMeta | null {
  const [resolvedMeta, setResolvedMeta] = useState<ResolvedMeta | null>(null)
  const { context } = useMetaContext()

  useLayoutEffect(() => {
    if (!routeMeta) return
    const { meta, pathname } = routeMeta
    const pendingMeta = meta(context)

    if (pendingMeta instanceof Observable) {
      const subscription = pendingMeta.subscribe((resolvedMeta) => {
        setResolvedMeta({
          pathname: routeMeta.pathname,
          data: resolvedMeta,
        })
      })

      return () => subscription.unsubscribe()
    }

    if (pendingMeta instanceof Promise) {
      pendingMeta.then((resolvedMeta) => {
        setResolvedMeta({
          pathname: routeMeta.pathname,
          data: resolvedMeta,
        })
      })
      return
    }

    setResolvedMeta({
      pathname,
      data: pendingMeta,
    })
  }, [routeMeta, context])

  return resolvedMeta
}
